import React, { useState } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_BASE_URL } from '../../utils/constants';
import './ContactForm.css';

const countries = [
  { code: '+91', name: 'India', flag: 'https://flagcdn.com/in.svg', id: 'IN' },
  { code: '+971', name: 'UAE', flag: 'https://flagcdn.com/ae.svg', id: 'AE' },
  { code: '+1', name: 'USA', flag: 'https://flagcdn.com/us.svg', id: 'US' },
  { code: '+44', name: 'UK', flag: 'https://flagcdn.com/gb.svg', id: 'GB' },
  { code: '+61', name: 'Australia', flag: 'https://flagcdn.com/au.svg', id: 'AU' },
  { code: '+65', name: 'Singapore', flag: 'https://flagcdn.com/sg.svg', id: 'SG' },
];

const ContactForm = () => {
  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [interest, setInterest] = useState('');
  const [message, setMessage] = useState('');

  // UI/Validation State
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Character Limit for Message
  const maxMessageLength = 500;

  const validateForm = () => {
    const tempErrors = {};
    if (!name.trim()) {
      tempErrors.name = 'Full Name is required';
    } else if (name.trim().length < 3) {
      tempErrors.name = 'Name must be at least 3 characters';
    }

    if (!email.trim()) {
      tempErrors.email = 'Email Address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      tempErrors.email = 'Please enter a valid email address';
    }

    if (!phoneDigits.trim()) {
      tempErrors.phone = 'Phone number is required';
    } else if (!/^\d{7,15}$/.test(phoneDigits.replace(/[\s-]/g, ''))) {
      tempErrors.phone = 'Please enter a valid phone number (7 to 15 digits)';
    }

    if (!interest) {
      tempErrors.interest = 'Please select what you are interested in';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleMessageChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxMessageLength) {
      setMessage(text);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please correct the errors in the form.');
      return;
    }

    setLoading(true);
    const fullPhoneNumber = `${selectedCountry.code}${phoneDigits.replace(/[\s-]/g, '')}`;

    const formData = {
      name: name.trim(),
      phno: fullPhoneNumber,
      email: email.trim(),
      subject: interest,
      message: message.trim(),
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/brookscontainer/contact-us`,
        formData
      );

      const successMsg = response.data.message || 'Thank you! Our team will connect with you soon.';
      toast.success(successMsg);

      // Reset form
      setName('');
      setEmail('');
      setPhoneDigits('');
      setInterest('');
      setMessage('');
      setErrors({});
    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMsg = error.response?.data?.message || 'There was an error submitting the form. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact-section-wrapper">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="contact-container-custom">
        <div className="contact-row-custom">
          
          {/* Left Column: Contact details */}
          <div className="contact-left-column">
            <div className="contact-badge-container">
              <span className="contact-badge-text">Contact Us</span>
              <span className="contact-badge-line"></span>
            </div>
            
            <h2 className="contact-main-title">
              Let's Connect and<br />
              Build Your <span className="highlight-orange">Container<br />Income.</span>
            </h2>
            
            <p className="contact-description-text">
              Have questions or ready to get started? Our team is here to help you turn your container into a consistent source of monthly returns.
            </p>
            
            <div className="contact-info-grid">
              
              {/* Call Us */}
              <div className="info-item-box">
                <div className="info-icon-container">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div className="info-content-container">
                  <h4 className="info-item-title">Call Us</h4>
                  <p className="info-item-value">
                    <a href="tel:+911234567890">+91 12345 67890</a>
                  </p>
                  <p className="info-item-subtext">Mon - Fri | 9:00 AM - 6:00 PM (IST)</p>
                </div>
              </div>
              
              {/* Email Us */}
              <div className="info-item-box">
                <div className="info-icon-container">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div className="info-content-container">
                  <h4 className="info-item-title">Email Us</h4>
                  <p className="info-item-value">
                    <a href="mailto:info@brookscontainer.com">info@brookscontainer.com</a>
                  </p>
                  <p className="info-item-subtext">We reply within 24 hours</p>
                </div>
              </div>
              
              {/* Head Office */}
              <div className="info-item-box">
                <div className="info-icon-container">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div className="info-content-container">
                  <h4 className="info-item-title">Head Office</h4>
                  <p className="info-item-subtext" style={{ fontSize: '13px', fontWeight: '600', color: '#001A3D' }}>
                    Brooks Container Pvt. Ltd. Unit No. 12, Logistics Park, Nhava Sheva, Navi Mumbai, Maharashtra - 400707, India
                  </p>
                </div>
              </div>
              
              {/* Global Presence */}
              <div className="info-item-box">
                <div className="info-icon-container">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                </div>
                <div className="info-content-container">
                  <h4 className="info-item-title">Global Presence</h4>
                  <p className="info-item-subtext" style={{ fontSize: '13px', fontWeight: '500' }}>
                    We operate across key global trade networks. Connect with us to know more.
                  </p>
                </div>
              </div>

            </div>
          </div>
          
          {/* Right Column: Form Card */}
          <div className="contact-right-column">
            <div className="contact-card-wrapper">
              <div className="contact-card-header">
                <h3 className="contact-card-title">Send Us a Message</h3>
                <p className="contact-card-subtitle">Fill out the form and our team will get back to you.</p>
              </div>
              
              <form onSubmit={handleSubmit} className="contact-form-container">
                
                {/* Name & Email Row */}
                <div className="form-row-two-col">
                  
                  {/* Name */}
                  <div className="form-field-group">
                    <label className="form-input-label">Full Name</label>
                    <div className="input-icon-wrapper">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <input
                        type="text"
                        placeholder="Enter your full name"
                        className={`form-text-input ${errors.name ? 'input-has-error' : ''}`}
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (errors.name) setErrors(prev => ({ ...prev, name: null }));
                        }}
                      />
                    </div>
                    {errors.name && <span className="field-error-message">{errors.name}</span>}
                  </div>
                  
                  {/* Email */}
                  <div className="form-field-group">
                    <label className="form-input-label">Email Address</label>
                    <div className="input-icon-wrapper">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        className={`form-text-input ${errors.email ? 'input-has-error' : ''}`}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) setErrors(prev => ({ ...prev, email: null }));
                        }}
                      />
                    </div>
                    {errors.email && <span className="field-error-message">{errors.email}</span>}
                  </div>
                  
                </div>
                
                {/* Phone Number */}
                <div className="form-field-group full-width">
                  <label className="form-input-label">Phone Number</label>
                  <div className={`phone-input-wrapper-custom ${errors.phone ? 'input-has-error' : ''}`}>
                    <div className="phone-country-selector">
                      <img src={selectedCountry.flag} alt={selectedCountry.name} className="country-flag-icon" />
                      <span className="country-code-text">{selectedCountry.code}</span>
                      <svg className="country-arrow-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                      
                      {/* Hidden Accessible Select */}
                      <select
                        value={selectedCountry.id}
                        onChange={(e) => {
                          const found = countries.find(c => c.id === e.target.value);
                          if (found) setSelectedCountry(found);
                        }}
                        className="hidden-select-element"
                      >
                        {countries.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <input
                      type="tel"
                      placeholder="Enter your phone number"
                      className="phone-digits-input"
                      value={phoneDigits}
                      onChange={(e) => {
                        setPhoneDigits(e.target.value);
                        if (errors.phone) setErrors(prev => ({ ...prev, phone: null }));
                      }}
                    />
                  </div>
                  {errors.phone && <span className="field-error-message">{errors.phone}</span>}
                </div>
                
                {/* Interest Dropdown */}
                <div className="form-field-group full-width">
                  <label className="form-input-label">I'm Interested In</label>
                  <div className="select-wrapper-custom">
                    <select
                      className={`select-dropdown-custom ${errors.interest ? 'input-has-error' : ''}`}
                      value={interest}
                      onChange={(e) => {
                        setInterest(e.target.value);
                        if (errors.interest) setErrors(prev => ({ ...prev, interest: null }));
                      }}
                    >
                      <option value="" disabled>Select an option</option>
                      <option value="Buy and Rent a Container">Buy and Rent a Container</option>
                      <option value="Lease a Container">Lease a Container</option>
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                    <svg className="select-chevron-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  {errors.interest && <span className="field-error-message">{errors.interest}</span>}
                </div>
                
                {/* Message Textarea */}
                <div className="form-field-group full-width">
                  <label className="form-input-label">Message</label>
                  <div className="textarea-wrapper-custom">
                    <textarea
                      placeholder="Tell us about your container requirement"
                      className="textarea-field-custom"
                      value={message}
                      onChange={handleMessageChange}
                    />
                    <span className="textarea-char-counter">
                      {message.length}/{maxMessageLength}
                    </span>
                  </div>
                </div>
                
                {/* Submit Button */}
                <button
                  type="submit"
                  className="submit-btn-custom"
                  disabled={loading}
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </>
                  )}
                </button>
                
                {/* Privacy Note */}
                <div className="form-privacy-note">
                  <svg className="privacy-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <p className="privacy-text">
                    Your information is secure and will never be shared with third parties.
                  </p>
                </div>

              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactForm;